import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import GlobalHeader from '../GlobalHeader';
import EditorToolbar from './EditorToolbar';
import CommonFields from './CommonFields';
import useAutoSave from './useAutoSave';
import PoemModule from './modules/PoemModule';
import StoryModule from './modules/StoryModule';
import QuoteModule from './modules/QuoteModule';
import ShayariModule from './modules/ShayariModule';
import ArticleModule from './modules/ArticleModule';
import GhazalModule from './modules/GhazalModule';
import DohaModule from './modules/DohaModule';
import EssayModule from './modules/EssayModule';
import BookModule from './modules/BookModule';
import PlayModule from './modules/PlayModule';
import ChildrenLiteratureModule from './modules/ChildrenLiteratureModule';
import MemoirModule from './modules/MemoirModule';
import { createSafeRichHTML } from '../../lib/sanitize';
import LiteraryReviewModule from './modules/LiteraryReviewModule';
import ResearchPaperModule from './modules/ResearchPaperModule';
import InterviewModule from './modules/InterviewModule';
import SpeechModule from './modules/SpeechModule';

const CONTENT_MODULES = {
  poem: PoemModule,
  story: StoryModule,
  quote: QuoteModule,
  shayari: ShayariModule,
  article: ArticleModule,
  ghazal: GhazalModule,
  doha: DohaModule,
  essay: EssayModule,
  book: BookModule,
  play: PlayModule,
  children_literature: ChildrenLiteratureModule,
  memoir: MemoirModule,
  literary_review: LiteraryReviewModule,
  research_paper: ResearchPaperModule,
  interview: InterviewModule,
  speech: SpeechModule,
};

export default function BaseEditor() {
  const { contentType: urlContentType, contentId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [contentType, setContentType] = useState(urlContentType || null);
  
  const [formData, setFormData] = useState({
    title: '',
    featured_image: '',
    category: '',
    tags: [],
    language: 'hindi',
    meta_title: '',
    meta_description: '',
    meta_keywords: [],
    slug: '',
  });
  
  const [metadata, setMetadata] = useState({});

  // Load existing content if editing
  useEffect(() => {
    if (contentId && contentId !== 'new') {
      loadContent();
    }
  }, [contentId]);

  const loadContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/content/${contentId}`,
        { withCredentials: true }
      );
      
      const content = response.data;
      
      // Set content type from loaded content
      setContentType(content.content_type);
      
      setFormData({
        title: content.title,
        featured_image: content.featured_image || '',
        category: content.category || '',
        tags: content.tags || [],
        language: content.language,
        meta_title: content.meta_title || '',
        meta_description: content.meta_description || '',
        meta_keywords: content.meta_keywords || [],
        slug: content.slug || '',
      });
      
      setMetadata(content.metadata || {});
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (isAutoSave = false) => {
    if (!formData.title || !formData.title.trim()) {
      if (!isAutoSave) {
        toast.error('Please enter a title');
      }
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        base: {
          ...formData,
          content_type: contentType,
          status: 'draft',
        },
        metadata,
      };

      if (contentId && contentId !== 'new') {
        // Update existing
        await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/api/content/${contentId}`,
          payload,
          { withCredentials: true }
        );
        if (!isAutoSave) {
          toast.success('Draft saved successfully');
        }
      } else {
        // Create new
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/content`,
          payload,
          { withCredentials: true }
        );
        
        const newContentId = response.data.content_id;
        if (!isAutoSave) {
          toast.success('Draft saved successfully');
        }
        // Navigate to edit mode
        navigate(`/editor/edit/${newContentId}`, { replace: true });
      }
    } catch (error) {
      console.error('Error saving content:', error);
      if (!isAutoSave) {
        toast.error(error.response?.data?.detail || 'Failed to save draft');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!formData.title || !formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    // Validate content type specific requirements
    if (contentType === 'poem' && (!metadata.stanzas || metadata.stanzas.length === 0)) {
      toast.error('Please add at least one stanza');
      return;
    }
    if (contentType === 'quote' && !metadata.quote_text) {
      toast.error('Please enter the quote text');
      return;
    }
    if (contentType === 'story' && !metadata.story_content) {
      toast.error('Please write the story content');
      return;
    }
    if (contentType === 'shayari' && (!metadata.couplets || metadata.couplets.length === 0)) {
      toast.error('Please add at least one couplet');
      return;
    }
    if (contentType === 'article' && !metadata.article_content) {
      toast.error('Please write the article content');
      return;
    }
    if (contentType === 'ghazal' && (!metadata.shers || metadata.shers.length === 0)) {
      toast.error('Please add at least one sher');
      return;
    }
    if (contentType === 'doha' && !metadata.doha_text) {
      toast.error('Please enter the doha text');
      return;
    }
    if (contentType === 'essay' && !metadata.essay_content) {
      toast.error('Please write the essay content');
      return;
    }
    if (contentType === 'book' && (!metadata.chapters || metadata.chapters.length === 0)) {
      toast.error('Please add at least one chapter');
      return;
    }

    try {
      setPublishing(true);
      
      // First save as draft
      await handleSave(true);
      
      // Then publish
      const currentId = contentId && contentId !== 'new' ? contentId : null;
      if (currentId) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/content/${currentId}/publish`,
          {},
          { withCredentials: true }
        );
        toast.success('Content published successfully!');
        navigate('/dashboard#published');
      } else {
        toast.error('Please save as draft first');
      }
    } catch (error) {
      console.error('Error publishing content:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish content');
    } finally {
      setPublishing(false);
    }
  };

  // AI Enhancement Handlers
  const handleAITitleSuggestions = (titles) => {
    // Show modal with titles for selection
    const selectedTitle = titles[0]; // For now, use first suggestion
    setFormData({ ...formData, title: selectedTitle });
  };

  const handleAIContentImprovement = (improvedContent) => {
    // Update the main content field based on content type
    if (contentType === 'story') {
      setMetadata({ ...metadata, story_content: improvedContent });
    } else if (contentType === 'article') {
      setMetadata({ ...metadata, article_content: improvedContent });
    } else if (contentType === 'essay') {
      setMetadata({ ...metadata, essay_content: improvedContent });
    }
    // For poetry types, would need more specific handling
  };

  const handleAISummaryGeneration = (summary) => {
    // Update summary/excerpt based on content type
    if (contentType === 'story' || contentType === 'book') {
      setMetadata({ ...metadata, summary: summary });
    } else if (contentType === 'article') {
      setMetadata({ ...metadata, excerpt: summary });
    } else if (contentType === 'essay') {
      setMetadata({ ...metadata, abstract: summary });
    }
  };

  const handleAITagsSuggestion = (tags) => {
    setFormData({ ...formData, tags: tags });
  };

  const handleAISEOGeneration = (seoData) => {
    setFormData({
      ...formData,
      meta_title: seoData.meta_title,
      meta_description: seoData.meta_description,
      meta_keywords: seoData.keywords
    });
  };

  const handleAIGrammarCheck = (grammarData) => {
    // Show results in a modal or update content with corrected version
    if (grammarData.error_count === 0) {
      toast.success('No grammar errors found!');
    } else {
      toast.info(`Found ${grammarData.error_count} issues. Score: ${grammarData.score}/100`, {
        duration: 5000
      });
      // Optionally show corrected text
      if (window.confirm(`Found ${grammarData.error_count} grammar issues. Apply corrections?`)) {
        handleAIContentImprovement(grammarData.corrected_text);
      }
    }
  };

  const handleAIReadabilityScore = (readabilityData) => {
    // Show readability results
    const message = `Readability: ${readabilityData.readability_score}/100\nLevel: ${readabilityData.reading_level}\nFlow: ${readabilityData.flow_score}/100`;
    toast.info(message, { duration: 6000 });
    
    if (readabilityData.suggestions && readabilityData.suggestions.length > 0) {
      console.log('Readability Suggestions:', readabilityData.suggestions);
    }
  };

  const handleAIQualityAnalysis = (qualityData) => {
    // Show comprehensive quality report
    const message = `Quality Grade: ${qualityData.grade}\nOverall: ${qualityData.overall_score}/100\nEngagement: ${qualityData.engagement}/100\nOriginality: ${qualityData.originality}/100`;
    toast.info(message, { duration: 8000 });
    
    console.log('Quality Analysis:', qualityData);
    console.log('Strengths:', qualityData.strengths);
    console.log('Weaknesses:', qualityData.weaknesses);
    console.log('Recommendations:', qualityData.recommendations);
  };

  // Auto-save hook
  useAutoSave(
    { ...formData, metadata },
    handleSave,
    30000 // Auto-save every 30 seconds
  );

  // Get the appropriate content module
  const ContentModule = CONTENT_MODULES[contentType];

  if (!ContentModule) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Invalid Content Type</h1>
          <button
            onClick={() => navigate('/editor')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
          >
            Back to Hub
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      
      <EditorToolbar
        onSave={() => handleSave(false)}
        onPublish={handlePublish}
        onPreview={() => setShowPreview(!showPreview)}
        showPreview={showPreview}
        onBack={() => navigate('/editor')}
        saving={saving}
        publishing={publishing}
        isDraft={true}
        contentType={contentType}
        title={formData.title}
        content={JSON.stringify(metadata)}
        metadata={metadata}
        onAITitleSuggestions={handleAITitleSuggestions}
        onAIContentImprovement={handleAIContentImprovement}
        onAISummaryGeneration={handleAISummaryGeneration}
        onAITagsSuggestion={handleAITagsSuggestion}
        onAISEOGeneration={handleAISEOGeneration}
      />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className={`grid ${showPreview ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-8`}>
          {/* Editor Panel */}
          <div className="space-y-8">
            {/* Common Fields */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
              <CommonFields
                formData={formData}
                onChange={setFormData}
                contentType={contentType}
              />
            </div>

            {/* Content Type Specific Fields */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4 capitalize">
                {contentType} Content
              </h2>
              <ContentModule
                metadata={metadata}
                onChange={setMetadata}
              />
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="lg:sticky lg:top-20 h-fit">
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Live Preview</h2>
                <div className="prose dark:prose-invert max-w-none">
                  {/* Preview content based on type */}
                  <PreviewContent
                    contentType={contentType}
                    formData={formData}
                    metadata={metadata}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewContent({ contentType, formData, metadata }) {
  if (!formData.title) {
    return <p className="text-muted-foreground italic">Start writing to see preview...</p>;
  }

  return (
    <div>
      {/* Title */}
      <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        {formData.title}
      </h1>

      {/* Featured Image */}
      {formData.featured_image && (
        <img
          src={formData.featured_image}
          alt={formData.title}
          className="w-full rounded-lg mb-6"
        />
      )}

      {/* Type-specific preview */}
      {contentType === 'poem' && (
        <div className="space-y-6">
          {(metadata.stanzas || []).map((stanza, i) => (
            <div key={i} className="whitespace-pre-wrap font-serif text-lg" style={{ fontFamily: 'Cormorant Garamond, serif', lineHeight: '1.8' }}>
              {stanza}
            </div>
          ))}
        </div>
      )}

      {contentType === 'quote' && metadata.quote_text && (
        <blockquote className="text-2xl italic border-l-4 border-primary pl-6 py-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          "{metadata.quote_text}"
          {metadata.author_name && (
            <footer className="text-base mt-2 text-muted-foreground not-italic">
              — {metadata.author_name}
            </footer>
          )}
        </blockquote>
      )}

      {contentType === 'shayari' && (
        <div className="space-y-4">
          {(metadata.couplets || []).map((couplet, i) => (
            <div key={i} className="text-lg font-serif whitespace-pre-wrap" style={{ fontFamily: 'Noto Sans Devanagari, serif', lineHeight: '2' }}>
              {couplet}
            </div>
          ))}
        </div>
      )}

      {(contentType === 'story' || contentType === 'article') && (
        <div
          className="prose-content"
          dangerouslySetInnerHTML={createSafeRichHTML(
            metadata.story_content || metadata.article_content || ''
          )}
        />
      )}

      {/* Tags */}
      {formData.tags && formData.tags.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {formData.tags.map((tag, i) => (
            <span key={i} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
